import { useState, useEffect, useCallback } from 'preact/hooks'
//import { MapboxStyleSwitcherControl } from 'mapbox-gl-style-switcher'

//import 'mapbox-gl-style-switcher/styles.css'
import './style-switcher.css'

import { useLocalStorageState } from '../../use-local-storage-state'
import { formatColorRule } from './Colors'

import { off, on } from '../../events'
import { setUserLocation, clearUserLocation } from '../../actions'

import { StyleSwitcher } from './style-switcher'
import { ColorLegend } from './color-legend'

export const useMap = (project, options = {}) => {
  const [container, setContainer] = useState()
  const [styleUri, setStyleUri] = useLocalStorageState(
    `fotoapp:${project.id}:map:styleUri`,
    'mapbox://styles/mapbox/outdoors-v11'
  )
  const [visibleLayers, setVisibleLayers] = useLocalStorageState(
    `fotoapp:${project.id}:visibleLayers`,
    []
  )

  const ref = useCallback((node) => {
    setContainer(node)
  }, [])

  const [map, setMap] = useState()

  const handleVisibleLayersChanged = (visibleLayers) => setVisibleLayers(visibleLayers)

  const handleMapStyleChanged = (style) => setStyleUri(style.uri)

  useEffect(() => {
    let timeout
    const handleResize = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        if (map) {
          map.resize()
        }
      }, 50)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [map])

  useEffect(() => {
    if (container) {
      console.log('init map')
      const map = new mapboxgl.Map({
        attributionControl: false,
        style: styleUri,
        ...options,
        container
      })

      if (project.colors.length > 0) {
        let legendContent = `
          <ul>
            ${project.colors
              .map((color) => {
                return `<li><div style="background-color: ${color.color}"></div>${formatColorRule(
                  color
                )}</li>`
              })
              .join('')}
              <li><div style="background-color: ${project.defaultColor}"></div>Övriga</li>
          </ul>
        `
        const legend = new ColorLegend(legendContent)
        map.addControl(legend)
      }

      const styleSwitcher = new StyleSwitcher(options.layers, styleUri, visibleLayers)
      map.addControl(styleSwitcher)

      on('visibleLayersChanged', handleVisibleLayersChanged)
      on('mapStyleChanged', handleMapStyleChanged)

      if (options.geoLocation) {
        const geolocation = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          fitBoundsOptions: {
            maxZoom: 18,
            animate: false
          },
          trackUserLocation: true
        })

        if (options.setUserLocation) {
          geolocation.on('geolocate', (e) => {
            const lat = e.coords.latitude
            const lng = e.coords.longitude
            setUserLocation({ lng, lat })
          })

          geolocation.on('trackuserlocationend', () => {
            if (geolocation._watchState === 'OFF') {
              clearUserLocation()
            }
          })
        }

        map.addControl(geolocation)
      }

      map.addControl(new mapboxgl.NavigationControl())

      if (options.geoJsonUrl) {
        map.on('style.load', function () {
          let strokeColor = 'white'

          map.addSource('points', {
            type: 'geojson',
            data: options.geoJsonUrl,
            generateId: true
          })
          map.addLayer({
            id: 'points',
            type: 'circle',
            source: 'points',
            paint: {
              // make circles larger as the user zooms from z10 to z22
              'circle-radius': {
                base: 3,
                stops: [
                  [10, 5],
                  [12, 7],
                  [17, 9]
                ]
              },
              'circle-opacity': [
                'case',
                ['==', ['get', 'id'], options.currentPointId || null],
                0.1,
                1
              ],
              'circle-color': ['get', 'Färg'],
              'circle-stroke-width': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                1.5,
                0.5
              ],
              'circle-stroke-color': strokeColor
            }
          })
        })
      }
      setMap(map)
      return () => {
        console.log('remove map')
        off('visibleLayersChanged', handleVisibleLayersChanged)
        off('mapStyleChanged', handleMapStyleChanged)
        map.remove()
      }
    }
  }, [container])

  return [ref, map]
}
